import {toast} from 'react-toastify'
import {useTranslation} from 'react-i18next'
import {CloseButton} from '@headlessui/react'

import {Button, createBaseDialogState, DialogNew} from '@sdox/ui'
import {useDisclaimerCrud} from '@sdox/api'

import {useCollectionId} from '../../hooks'

import type {XcDisclaimerInterfaceFragment} from '@sdox/api'

type DeleteData = Pick<XcDisclaimerInterfaceFragment, 'id'>

export const useDeleteDisclaimerDialogStore = createBaseDialogState<DeleteData>()

export const DeleteDisclaimerDialog = () => {
  const collectionId = useCollectionId()
  const {deleteDisclaimer} = useDisclaimerCrud(collectionId)
  const {isDialogOpen, isLoading, toggleIsLoading, closeDialog, data} = useDeleteDisclaimerDialogStore()
  const {t} = useTranslation()

  const onClickDelete = async (): Promise<void> => {
    try {
      toggleIsLoading()
      await deleteDisclaimer(data?.id ?? '')
      toast.success(t('common:deleted', {item: 'Disclaimer'}))
      closeDialog()
    } catch (e) {
      console.error(e)
      toast.error(t('common:error.delete', {item: 'Disclaimer'}))
    } finally {
      toggleIsLoading()
    }
  }

  return (
    <DialogNew
      heading={t('common:deleteItem', {item: 'Disclaimer'})}
      open={isDialogOpen}
      onClose={closeDialog}
      size={'small'}
      persistent={isLoading}
    >
      <DialogNew.Content>
        <div className={'mb-spacing-20'}>
          <p className={'mb-spacing-12 text-p1'}>
            {t('common:confirmDeletion', {item: 'Disclaimer'})}
          </p>
          <p className={'text-p1'}>
            {t('common:noUndo')}
          </p>
        </div>
      </DialogNew.Content>
      <DialogNew.Footer className={'flex justify-end gap-spacing-16'}>
        <CloseButton
          as={Button}
          size={'medium'}
          variant={'flat'}
          onClick={closeDialog}
        >
          {t('common:cancel')}
        </CloseButton>
        <Button
          size={'medium'}
          variant={'filled-brand'}
          onClick={onClickDelete}
          loading={isLoading}
        >
          {t('common:delete')}
        </Button>
      </DialogNew.Footer>
    </DialogNew>
  )
}
